import * as React from "react";
import { Theme } from "@mui/material/styles";
import { Box, Button, Divider, Typography } from "@mui/material";
import {
  KeyValue,
  Query,
  SearchPillData,
  SimpleTableRow,
  Sort,
  TableHeader,
} from "../../../interfaces";
import { apiPost, fetchData } from "../../../common/services/ApiClient";
import { ApiBaseUrl } from "../../../config";
import MultiHeaderTable from "../../MultiHeaderTable/MultiHeaderTable";
import SearchPills from "../../../containers/SearchPills";
import {
  fileNameFormat,
  sortObjValue,
} from "../../../common/services/Utilities";
import SimpleTableBody from "../../MultiHeaderTable/SimpleTableBody";
import ApiDataDownload, { apiDownloadProps } from "../../ApiDataDownload";
import {
  summaryTableHeader,
  transformStateName,
} from "./SummaryTableUtilities";
import LoadingIndicator from "../../LoadingIndicator";
import LeavingFDICDialog from "../../LeavingFDICDialog";
import ModalDialog from "../../ModalDialog";
import BranchOfficeDeposits from "../../../containers/BranchOfficeDeposits";
import ShowHideButton from "../../ShowHideButton";

interface Props {
  appState: Query;
  updateFilter: (field: string, value: string) => void;
}

export default function SummaryTablesTop50({ appState, updateFilter }: Props) {
  const isBHC = appState.ui.reportType === "top50BHC";

  const top50AppState = React.useMemo(() => {
    return {
      filters: appState.filters,
      search: appState.search,
      ui: appState.ui,
      sort: appState.sort,
      pagination: appState.pagination,
    };
  }, [
    appState.filters,
    appState.pagination,
    appState.search,
    appState.sort,
    appState.ui,
  ]);

  const [fetchAggCount, setFetchAggCount] = React.useState(false);
  const [top50Data, setTop50Data] = React.useState([{}]);

  const [certsOrRSSDHCR, setCertsOrRSSDHCR] = React.useState([]);
  const [aggregatedData, setAggregatedData] = React.useState([{}]);
  const [initialLoad, setInitialLoad] = React.useState(true);
  const [previousAppState, setPreviousAppState] = React.useState(top50AppState);
  const [sort, setSort] = React.useState<Sort>({
    sortField: "sum_DEPSUMBR",
    sortOrder: "DESC",
  });
  const [previousSort, setPreviousSort] = React.useState(sort);

  const [loading, setLoading] = React.useState(false);
  const queryKey = isBHC ? "RSSDHCR" : "CERT";
  const [open, setOpen] = React.useState("");
  const [name, setName] = React.useState("");
  const [instTypeVal, setInstTypeVal] = React.useState("");
  const [certVal, setCertVal] = React.useState("");
  const [bhcRSSDVal, setBhcRSSDVal] = React.useState("");

  const branchOfficeModal = (
    <ModalDialog
      title={name}
      display={open !== ""}
      setDisplay={() => setOpen("")}
      maxWidth="lg"
      onClose={() => {
        updateFilter("county", "");
        updateFilter("stalpbr", "");
      }}
    >
      <BranchOfficeDeposits
        providedAppState={{
          ...appState,
          filters: { ...appState.filters, cert: certVal, bhcRSSD: bhcRSSDVal },
          ui: { ...appState.ui, institutionType: instTypeVal },
        }}
      />
    </ModalDialog>
  );

  const top50Metadata: KeyValue[] = [
    {
      title: "Institution Name",
      apiField: "NAMEFULL",
      styles: { textAlign: "left", width: 220, minWidth: 220 },
    },
    {
      title: "CERT",
      apiField: "CERT",
      styles: { textAlign: "center", width: 200, minWidth: 200 },
    },
    {
      title: "Main Office Location",
      apiField: "STNAME",
      styles: { textAlign: "center", width: 200, minWidth: 200 },
    },
    {
      title: "Number of Offices",
      apiField: "count",
      styles: { textAlign: "center", width: 120 },
    },
    {
      title: "Total Deposits (Thousands USD)",
      apiField: "sum_DEPSUMBR",
      styles: { textAlign: "right", width: 200, minWidth: 200 },
    },
  ];

  const bhcHeaderMetaData: KeyValue[] = [
    {
      title: "Bank Holding Company Name",
      apiField: "NAMEHCR",
      styles: { textAlign: "left" },
    },
    {
      title: "BHC ID",
      apiField: "RSSDHCR",
      styles: { textAlign: "center" },
    },
    {
      title: "State Headquartered",
      apiField: "STALPHCR",
      styles: {},
    },
    {
      title: "Number of Offices",
      apiField: "count",
      styles: {},
    },
    {
      title: "Total Combined Deposits* (Thousands USD)",
      apiField: "sum_DEPSUMBR",
      styles: { textAlign: "right", width: 200 },
    },
  ];

  const currentMetaData = isBHC ? bhcHeaderMetaData : top50Metadata;
  const transformedData = (tableData: KeyValue[]) => {
    return tableData.map((data: KeyValue) => {
      const tableCellData: SimpleTableRow | KeyValue = {};

      currentMetaData.forEach((value: KeyValue) => {
        const apiField = value.apiField;
        const stateTransform = !data[apiField]
          ? "Foreign **"
          : transformStateName(data[apiField]);

        tableCellData[apiField ? apiField : value.title] = {
          value:
            (apiField === "count" || apiField === "sum_DEPSUMBR") &&
            data[apiField]
              ? data[apiField].toLocaleString()
              : apiField === "STALPHCR"
                ? stateTransform
                : data[apiField],
          styles: value.styles,
        };
      });
      tableCellData.rowOnClick = (e: any) => {
        if (
          (e.key === "Enter" || e.nativeEvent.type === "click") &&
          appState.filters.lastYear
        ) {
          if (tableCellData["RSSDHCR"]) {
            setBhcRSSDVal(`${tableCellData.RSSDHCR.value}`);
            setCertVal("");
            setOpen(`${tableCellData.RSSDHCR.value}`);
            setInstTypeVal("bhcs");
            setName(`${tableCellData.NAMEHCR.value}`);
          } else {
            setCertVal(`${tableCellData.CERT.value}`);
            setBhcRSSDVal("");
            setOpen(`${tableCellData.CERT.value}`);
            setInstTypeVal("banks");
            setName(`${tableCellData.NAMEFULL.value}`);
          }
        }
      };
      return tableCellData;
    });
  };

  const top50Header: TableHeader[][] = [
    currentMetaData.map((element) => ({
      title: element.title,
      styles: element.styles,
      colSpan: 1,
      apiField: element.apiField,
    })),
  ];

  const downloadHeaders = isBHC
    ? [
        { label: "Report Period", key: "YEAR" },
        { label: "Bank Holding Company Name", key: "NAMEHCR" },
        { label: "BHC ID", key: "RSSDHCR" },
        { label: "State Headquartered", key: "STALPHCR" },
        { label: "Number of Offices", key: "count" },
        {
          label: "Total Combined Deposits*(Thousands USD)",
          key: "sum_DEPSUMBR",
        },
      ]
    : [
        { label: "Report Period", key: "YEAR" },
        { label: "Institution Name", key: "NAMEFULL" },
        { label: "Cert", key: "CERT" },
        { label: "Main Office Location", key: "STNAME" },
        { label: "Number of Offices", key: "count" },
        { label: "Total Deposits (Thousands USD)", key: "sum_DEPSUMBR" },
      ];

  const apiData: apiDownloadProps[] = [
    {
      appState,
      fileName: `${fileNameFormat("Top_50_raw")}.csv`,
      apiPath: "sod",
      text: "Download Results Data - raw",
    },
    {
      appState,
      fileName: `${fileNameFormat("Top_50_formatted")}.csv`,
      apiPath: "sod",
      text: "Download Results Data - formatted",
      headers: downloadHeaders,
      footnotes: isBHC
        ? [
            " *The total deposits for the Bank Holding Company is the combined Total Domestic Deposits of the underlying Insured Subsidiaries.",
            "** Denotes that the institution is chartered in a foreign county.",
            "Note about this data: This search will return results for top regulatory bank holding companies and their FDIC-Insured subsidiaries. The regulatory top holder is assigned by the Federal Reserve Board based on ownership and control percentages. Thrift bank holding companies information is not available. Official bank holding company information can be found by visiting the website of the Federal Reserve System - National Information Center",
          ]
        : ([
            "Includes deposits of all FDIC-Insured Institutions in domestic offices (50 states and DC), Puerto Rico, and U.S. Territories",
            appState.filters.lastYear === "1994"
              ? "*Includes 18 conservatorships"
              : null,
          ].filter(Boolean) as string[]),
      formattedData: aggregatedData.map((data: KeyValue) => {
        return {
          ...data,
          YEAR: `6/30/${appState.filters.lastYear}`,
          sum_DEPSUMBR: data.sum_DEPSUMBR?.toLocaleString(),
          count: data.count?.toLocaleString(),
          STALPHCR: !data.STALPHCR
            ? "Foreign **"
            : transformStateName(data.STALPHCR),
        };
      }),
    },
  ];

  React.useEffect(() => {
    let isMounted = true;
    if (
      JSON.stringify(top50AppState) !== JSON.stringify(previousAppState) ||
      initialLoad
    ) {
      setLoading(true);
      initialLoad && setInitialLoad(false);
      setPreviousAppState(appState.filters.lastYear);

      const additionalFilters = isBHC
        ? " AND RSSDHCR:>0&get_unique=RSSDHCR"
        : "";
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/sod?filters=YEAR:${
            appState.filters.lastYear
          } AND BKMO:1${additionalFilters}&fields=CERT,NAMEFULL,STNAME,RSSDHCR,NAMEHCR,STALPHCR&sort_by=DEPDOM&sort_order=desc&limit=${1000}`,
        )
          .then((response) => {
            if (isMounted) {
              setTop50Data(response.data);
              setCertsOrRSSDHCR(
                response.data.map((res: KeyValue) => res[queryKey]),
              );
            }
          })
          .catch((error: Error) => console.error(error))
          .finally(() => {
            isMounted = false;
            setFetchAggCount(true);
          });
      })();
    }
  }, [appState, initialLoad, isBHC, previousAppState, queryKey, top50AppState]);

  React.useEffect(() => {
    let isMounted = true;

    if (isMounted && fetchAggCount && certsOrRSSDHCR.length) {
      setFetchAggCount(false);
      const aggregatePostReq = apiPost({
        filters: `YEAR:${
          appState.filters.lastYear
        } AND ${queryKey}:(${certsOrRSSDHCR.join(" OR ")})`,
        agg_by: queryKey,
        agg_sum_fields: "DEPSUMBR",
        agg_limit: 1000,
      });
      (async () => {
        await fetchData(`${ApiBaseUrl()}/sod?`, aggregatePostReq)
          .then((response) => {
            const key = isBHC ? "RSSDHCR" : "CERT";
            const top50Count = top50Data.map((data: KeyValue) => {
              const matchingObj = response.data.find(
                (res: KeyValue) => res[key] === data[key],
              );
              return { ...data, ...matchingObj };
            });

            setAggregatedData(
              sortObjValue(top50Count, "sum_DEPSUMBR", true).slice(0, 50),
            );
          })
          .catch((error: Error) => console.error(error))
          .finally(() => setLoading(false));
      })();
      return () => {
        isMounted = false;
        setFetchAggCount(false);
      };
    }
  }, [
    appState.filters.lastYear,
    certsOrRSSDHCR,
    fetchAggCount,
    isBHC,
    queryKey,
    top50Data,
  ]);

  const tableBody = loading ? (
    <LoadingIndicator
      text="Loading"
      spinnerSize={32}
      textSx={{
        fontSize: 20,
        width: "100%",
        textAlign: "center",
      }}
      spinnerSx={{
        marginTop: 24,
        width: "100%",
        textAlign: "center",
      }}
      table
    />
  ) : (
    <SimpleTableBody tableData={transformedData(aggregatedData)} />
  );
  const summaryTablesSearchPills: SearchPillData[] = [
    {
      label: "Reporting Period",
      value: `06/30/${appState.filters.lastYear}`,
      search: false,
      static: true,
      formKey: "lastYear",
    },
  ];

  const searchPills = (
    <ShowHideButton
      title="Selected Search Criteria"
      body={<SearchPills dataArray={summaryTablesSearchPills} />}
    />
  );
  const headingTitle = (
    <Box
      sx={{
        marginTop: -1,
        display: "flex",
        alignItems: "end",
        justifyContent: "space-between",
      }}
    >
      <Typography
        sx={{
          color: (theme) => theme.palette.primary.main,
          fontSize: 26,
          fontWeight: "bold",
          paddingBottom: "1rem",
        }}
      >
        {summaryTableHeader(appState.ui.reportType)}
      </Typography>
      <ApiDataDownload
        limit={50}
        app="SOD"
        apiData={apiData}
        apiPathLink={"sod"}
        post={{
          filters: `YEAR:${
            appState.filters.lastYear
          } AND BKMO:1 AND ${queryKey}:(${aggregatedData
            .map((data: KeyValue) => data[isBHC ? "RSSDHCR" : "CERT"])
            .join(" OR ")}) ${isBHC ? " AND RSSDHCR:>0" : ""}`,
          get_unique: isBHC ? "RSSDHCR" : "",
        }}
        commaSeparatedNumbers={["DEPSUMBR"]}
      />
    </Box>
  );

  function sortHandler(tableHeader: TableHeader) {
    if (tableHeader.apiField) {
      const sortDirection =
        sort.sortField === tableHeader.apiField && sort.sortOrder === "ASC"
          ? "DESC"
          : "ASC";
      setSort({ sortField: tableHeader.apiField, sortOrder: sortDirection });
    }
  }

  React.useEffect(() => {
    if (JSON.stringify(sort) !== JSON.stringify(previousSort)) {
      setPreviousSort(sort);
      setAggregatedData(
        sortObjValue(aggregatedData, sort.sortField, sort.sortOrder === "DESC"),
      );
    }
  }, [aggregatedData, previousSort, sort]);

  const footerSx = {
    color: (theme: Theme) => theme.palette.primary.main,
    width: 700,
    minWidth: 700,
    fontSize: 16,
    whiteSpace: "nowrap",
  };

  return (
    <>
      {headingTitle}
      <Divider />
      {searchPills}
      {branchOfficeModal}
      {isBHC && (
        <Button
          sx={{
            "fontWeight": "bold",
            "color": "#0000FF",
            "padding": 0,
            "marginTop": 0,
            "fontSize": "1rem",
            "&.MuiButton-root:hover, &.MuiButton-root:focus-visible": {
              backgroundColor: "#fff",
              textDecoration: "underline",
            },
          }}
          onClick={() => window.scrollTo(0, document.body.scrollHeight)}
        >
          Click for important information about this data.
        </Button>
      )}
      <div style={{ marginTop: ".5rem" }}>
        <MultiHeaderTable
          useStickyHeader
          tableBody={tableBody}
          tableHeaders={top50Header}
          sortProperties={{
            sortDirection: sort.sortOrder,
            sortField: sort.sortField,
            sortHandler,
          }}
        />
      </div>
      {isBHC ? (
        <>
          <Typography sx={footerSx}>
            *The total deposits for the Bank Holding Company is the combined
            Total Domestic Deposits of the underlying Insured Subsidiaries.
          </Typography>
          <Typography sx={footerSx}>
            ** Denotes that the institution is chartered in a foreign county.
          </Typography>
          <Typography
            variant="body2"
            id="bhc-note"
            color="primary.main"
            style={{ fontWeight: "bold", paddingTop: "1rem", paddingBottom: 0 }}
          >
            Note about this data:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              "& div.MuiTypography-root": {
                display: "inline",
                fontSize: "16px",
              },
              "paddingBottom": "1rem",
            }}
            color="primary.main"
            component={"div"}
          >
            This search will return results for top regulatory bank holding
            companies and their FDIC-Insured subsidiaries. The regulatory top
            holder is assigned by the Federal Reserve Board based on ownership
            and control percentages. Thrift bank holding companies information
            is not available. Official bank holding company information can be
            found by visiting the website of the{" "}
            <LeavingFDICDialog
              webAddress={"https://www.federalreserveconsumerhelp.gov/"}
              label={"Federal Reserve System - National Information Center."}
              noHover
            />
          </Typography>
        </>
      ) : (
        <>
          <Typography sx={footerSx}>
            Includes deposits of all FDIC-Insured Institutions in domestic
            offices (50 states and DC), Puerto Rico, and U.S. Territories
          </Typography>
          {appState.filters.lastYear === "1994" && (
            <Typography sx={footerSx}>
              * Includes 18 conservatorships
            </Typography>
          )}
        </>
      )}
    </>
  );
}
